import React from 'react';
import { useNavigate } from 'react-router-dom';
import { SEOHead } from '../../components/seo/SEOHead';
import { FAQSchema, BreadcrumbSchema, ServiceSchema } from '../../components/seo/SchemaMarkup';
import { Breadcrumbs } from '../../components/seo/Breadcrumbs';
import { LatestArticles } from '../../components/blog/LatestArticles';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { MapPin, Clock, Heart, Sparkles, ArrowRight, Music } from 'lucide-react';

export default function LadiesNightJamshedpur() {
  const navigate = useNavigate();

  const faqs = [
    {
      question: "Is there a ladies night at Scratch Unleashed Jamshedpur?",
      answer: "Yes! Scratch Unleashed hosts Ladies Night events on select weekdays and occasionally on weekends. Ladies enjoy complimentary entry and special offers on select drinks. Follow our Instagram @scratchunleashed or check our events page for the current ladies night schedule."
    },
    {
      question: "Is Scratch Unleashed safe for women?",
      answer: "Absolutely. Scratch Unleashed is Jamshedpur's safest and most professionally managed nightclub. We have strict security at the entrance, in-venue security personnel, CCTV monitoring, and a zero-tolerance policy for harassment. Our staff are trained to handle any situation discreetly and efficiently."
    },
    {
      question: "Can girls go to Scratch Unleashed without male companions?",
      answer: "Yes, women can absolutely visit Scratch Unleashed without male companions. Solo women and all-female groups are warmly welcomed. Our venue is known for being inclusive and safe for everyone."
    },
    {
      question: "What is the couple entry policy?",
      answer: "Couples are always welcome at Scratch Unleashed. On regular nights, couple entry is available at standard pricing. Special couple packages with reserved seating, welcome drinks, and personalised touches are available — ideal for date nights and anniversaries."
    },
    {
      question: "What should women wear to Scratch Unleashed?",
      answer: "Smart casual to formal attire is recommended. Dresses, skirts, and stylish casual wear all work well. There is no strict dress code for women, but neat and stylish is always appreciated. Comfort is important since you'll be dancing!"
    }
  ];

  const breadcrumbItems = [
    { name: 'Ladies Night Jamshedpur', url: null }
  ];

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)]">
      <SEOHead
        title="Ladies Night Jamshedpur | Girls Night Out - Scratch Unleashed"
        description="Best ladies night in Jamshedpur at Scratch Unleashed. Safe venue, complimentary entry for ladies, special drink offers, couple entry. Bindal Mall, Sonari. Book now!"
        keywords="ladies night Jamshedpur, girls night out Jamshedpur, couple entry Jamshedpur, ladies free entry Jamshedpur, women friendly nightclub Jamshedpur, safe nightclub Jamshedpur, ladies night club Jamshedpur"
        canonical="https://scratchjsr.com/ladies-night-jamshedpur"
      />
      <FAQSchema faqs={faqs} />
      <ServiceSchema
        serviceName="Ladies Night & Couple Entry"
        serviceDescription="Special ladies night events and couple entry packages at Scratch Unleashed, Jamshedpur's safest and most welcoming nightclub."
        serviceUrl="https://scratchjsr.com/ladies-night-jamshedpur"
      />
      <BreadcrumbSchema items={[
        { name: 'Home', url: 'https://scratchjsr.com/' },
        { name: 'Ladies Night Jamshedpur', url: 'https://scratchjsr.com/ladies-night-jamshedpur' }
      ]} />

      <div className="h-16"></div>

      <div className="max-w-[1120px] mx-auto px-4 sm:px-6 py-12">
        <Breadcrumbs items={breadcrumbItems} />

        <div className="mb-12">
          <h1 className="font-bebas text-5xl sm:text-6xl lg:text-7xl mb-4">
            LADIES NIGHT JAMSHEDPUR
          </h1>
          <p className="text-xl text-[var(--text-dim)] mb-6">
            Jamshedpur's Safest & Most Vibrant Night Out for Women
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button className="btn-crimson-glow rounded-xl" onClick={() => navigate('/checkin')}>
              Reserve Your Table
              <ArrowRight size={16} className="ml-2" />
            </Button>
            <Button variant="outline" className="rounded-xl border-[var(--border-custom)]" onClick={() => navigate('/events')}>
              See Upcoming Events
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-8">
            <section>
              <h2 className="font-bebas text-3xl mb-4">Best Ladies Night in Jamshedpur</h2>
              <div className="space-y-4 text-[var(--text-dim)] leading-relaxed">
                <p>
                  Looking for a great <strong>ladies night in Jamshedpur</strong>? Scratch Unleashed is the city's top choice for women seeking a premium, safe, and genuinely fun night out. Whether you're planning a <strong>girls night out in Jamshedpur</strong>, celebrating a birthday with your girlfriends, or simply looking for an evening of great music and good company, Scratch Unleashed delivers every time.
                </p>
                <p>
                  Our <strong>ladies night events</strong> feature complimentary entry for women, special pricing on select drinks, and the full Scratch Unleashed experience — world-class DJs, professional lighting, premium sound, and an atmosphere that's electric but never uncomfortable. We know what makes a great night out for women, and we've built our venue around those standards.
                </p>
              </div>
            </section>

            <section>
              <h2 className="font-bebas text-3xl mb-4">Safety First — Always</h2>
              <div className="space-y-4 text-[var(--text-dim)] leading-relaxed">
                <p>
                  When searching for a <strong>safe nightclub in Jamshedpur</strong> for women, Scratch Unleashed consistently tops the list. Our commitment to safety is non-negotiable. We operate trained security at the entrance, maintain visible security personnel throughout the venue, and have CCTV monitoring across all areas.
                </p>
                <p>
                  Our zero-tolerance policy for harassment means any complaint is addressed immediately and decisively. Female guests can approach any staff member at any time if they feel uncomfortable, and our team is trained to respond discreetly. We've built a reputation as the <strong>most women-friendly nightclub in Jamshedpur</strong> — and we take that seriously.
                </p>
              </div>
            </section>

            <section>
              <h2 className="font-bebas text-3xl mb-4">Couple Entry in Jamshedpur</h2>
              <div className="space-y-4 text-[var(--text-dim)] leading-relaxed">
                <p>
                  Scratch Unleashed is also Jamshedpur's number one venue for <strong>couple entry</strong> and date nights. Our cozy two-top tables, intimate seating areas, and premium service create the perfect backdrop for a romantic evening. Whether it's your first date or your tenth anniversary, our venue delivers a setting that feels special.
                </p>
                <p>
                  Couple packages include reserved seating with a welcome drink on arrival, personalised service, and optional add-ons like flower arrangements or custom birthday desserts. Booking in advance ensures you get the best tables.
                </p>
              </div>
            </section>

            <section className="bg-[var(--surface)] border border-[var(--border-custom)] rounded-2xl p-8">
              <h2 className="font-bebas text-3xl mb-4">Plan Your Girls' Night Out</h2>
              <p className="text-[var(--text-dim)] mb-6">
                Reserve your table now for the next ladies night. Join us at Scratch Unleashed — Jamshedpur's safest and most stylish nightclub.
              </p>
              <Button className="btn-crimson-glow rounded-xl" onClick={() => navigate('/checkin')}>
                Book Your Table
              </Button>
            </section>

            <section>
              <h2 className="font-bebas text-3xl mb-6">Frequently Asked Questions</h2>
              <Accordion type="single" collapsible className="space-y-3">
                {faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`} className="border border-[var(--border-custom)] rounded-xl px-4 bg-[var(--surface)]">
                    <AccordionTrigger className="hover:no-underline text-left">
                      <span className="font-semibold">{faq.question}</span>
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-[var(--text-dim)] pb-4">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>
          </div>

          <div className="lg:col-span-1 space-y-6">
            <Card className="p-6 bg-[var(--surface)] border border-[var(--border-custom)] rounded-xl">
              <h3 className="font-bebas text-2xl mb-4">Ladies Night Info</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Heart size={20} className="text-[var(--accent-gold)] mt-1" />
                  <div>
                    <p className="font-semibold text-sm">Ladies Entry</p>
                    <p className="text-sm text-[var(--text-dim)]">Complimentary on ladies night events</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock size={20} className="text-[var(--accent-gold)] mt-1" />
                  <div>
                    <p className="font-semibold text-sm">Opening Hours</p>
                    <p className="text-sm text-[var(--text-dim)]">Fri-Sun: 6 PM onwards</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin size={20} className="text-[var(--accent-gold)] mt-1" />
                  <div>
                    <p className="font-semibold text-sm">Location</p>
                    <p className="text-sm text-[var(--text-dim)]">3rd Floor, Bindal Mall, Sonari, Jamshedpur</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Sparkles size={20} className="text-[var(--accent-gold)] mt-1" />
                  <div>
                    <p className="font-semibold text-sm">Couple Entry</p>
                    <p className="text-sm text-[var(--text-dim)]">Special packages available</p>
                  </div>
                </div>
              </div>
            </Card>

            <LatestArticles maxPosts={3} />

            <Card className="p-6 bg-[var(--surface)] border border-[var(--border-custom)] rounded-xl">
              <h3 className="font-bebas text-2xl mb-4">Explore More</h3>
              <div className="space-y-3">
                <button onClick={() => navigate('/night-club-in-jamshedpur')} className="w-full text-left text-sm text-[var(--accent-gold)] hover:underline">
                  → Night Club in Jamshedpur
                </button>
                <button onClick={() => navigate('/bar-in-jamshedpur')} className="w-full text-left text-sm text-[var(--accent-gold)] hover:underline">
                  → Bar in Jamshedpur
                </button>
                <button onClick={() => navigate('/birthday-party-venue-jamshedpur')} className="w-full text-left text-sm text-[var(--accent-gold)] hover:underline">
                  → Birthday Party Venue
                </button>
                <button onClick={() => navigate('/private-party-venue-jamshedpur')} className="w-full text-left text-sm text-[var(--accent-gold)] hover:underline">
                  → Private Events
                </button>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
